import { z } from 'zod';
import { AskCite } from './ask.js';
/**
 * L10 Prep Brief — the Sunday-evening / Monday-morning pre-meeting
 * outline the agent generates before the weekly L10 fires. Five
 * sections, each a short bulleted list with optional cited evidence:
 *
 *   1. **Headlines** — what shipped / what slipped / what changed
 *   2. **Scorecard slips** — measurables off-target this week
 *   3. **Issues to discuss** — IDS candidates ordered by urgency
 *   4. **Rocks update** — quarterly Rock progress + flagged risks
 *   5. **People notes** — kudos, concerns, hiring loop signals
 *
 * Each bullet carries optional `cites` (the same `AskCite` shape the
 * Ask surface uses) and optional `severity` so the UI can render
 * status pills (`watch` / `critical`) when warranted.
 *
 * Storage: not persisted server-side today — generated on-demand via
 * `POST /api/orgs/:o/l10/prep-brief`. Per-org cron-driven generation
 * (Sunday 18:00 local) is a Phase-5 follow-up; the schema is shaped
 * for that future caching path with `orgId` + `weekStart` + `generatedAt`
 * already on the doc.
 */
/**
 * Severity hint for a single bullet. `info` is the routine default
 * (omit for unflagged bullets); `watch` is "keep an eye on this";
 * `critical` is "discuss in the IDS segment".
 */
export const L10PrepSeverity = z.enum(['info', 'watch', 'critical']);
/**
 * One bullet within a brief section. Headline + optional body + optional
 * severity + optional cites. Bodies are 1-2 sentences; the headline is
 * the one-line takeaway.
 */
export const L10PrepBullet = z.object({
    headline: z.string().min(1).max(200),
    body: z.string().max(800).optional(),
    severity: L10PrepSeverity.optional(),
    cites: z.array(AskCite).max(8).default([]),
});
/**
 * The five sections that make up a brief. Each is a bullet list; an
 * empty array means "nothing to flag in this section." The UI hides
 * fully-empty sections and surfaces a "Quiet week" placeholder when all
 * five are empty.
 */
export const L10PrepSections = z.object({
    headlines: z.array(L10PrepBullet).max(8).default([]),
    scorecardSlips: z.array(L10PrepBullet).max(8).default([]),
    issuesToDiscuss: z.array(L10PrepBullet).max(8).default([]),
    rocksUpdate: z.array(L10PrepBullet).max(8).default([]),
    peopleNotes: z.array(L10PrepBullet).max(8).default([]),
});
/**
 * The complete L10 prep brief. `confidence` is a single 0..1 confidence
 * across the whole synthesis (the agent's self-assessment of how well
 * the input data supports the bullets); UI surfaces it next to the
 * "Generated <date>" footer.
 */
export const L10PrepBrief = z.object({
    orgId: z.string().min(1),
    /** ISO date (YYYY-MM-DD) for the Monday anchoring the L10. */
    weekStart: z.string(),
    sections: L10PrepSections,
    confidence: z.number().min(0).max(1),
    /** ISO timestamp the brief was generated. */
    generatedAt: z.string(),
    /** Model id that produced the brief; absent on the deterministic mock path. */
    model: z.string().optional(),
});
/** Body for `POST /api/orgs/:o/l10/prep-brief`. */
export const L10PrepBriefRequest = z.object({
    /**
     * Optional ISO date (YYYY-MM-DD). Server defaults to "next Monday"
     * (or this Monday if today is Monday) so a CEO running the request
     * Sunday night gets tomorrow's brief without having to compute a date.
     */
    weekStart: z.string().optional(),
});
